import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const REVIEW_FILTERS = [["pending", "Pending"], ["approved", "Approved"], ["declined", "Declined"], ["all", "All"]];

// Keeps the Member column pinned while a wide table scrolls sideways (phones).
export const STICKY_CELL = "sticky left-0 z-10 bg-white shadow-[1px_0_0_0_#e2e8f0]";
const STICKY_HEAD = "sticky left-0 z-10 bg-slate-50 shadow-[1px_0_0_0_#e2e8f0]";

// groups: { pending: [...statuses], approved: [...], declined: [...] } — "all" matches everything.
export const inGroup = (groups, f, status) => f === "all" || (groups[f] || []).includes(status);
export const groupCounts = (groups, items, filters = REVIEW_FILTERS) =>
  Object.fromEntries(filters.map(([k]) => [k, items.filter((x) => inGroup(groups, k, x.status)).length]));

// Client-side lists: reveal 50 rows at a time as the coach scrolls.
export function useReveal(list, step = 50) {
  const [n, setN] = useState(step);
  useEffect(() => { setN(step); }, [list, step]);
  const loadMore = useCallback(() => setN((x) => x + step), [step]);
  return { visible: useMemo(() => list.slice(0, n), [list, n]), hasMore: list.length > n, loadMore };
}

export function ReviewFilters({ value, onChange, counts = {}, testId, filters = REVIEW_FILTERS }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-filters`}>
      {filters.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testId}-filter-${k}`} aria-pressed={value === k}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${value === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
          {label} <span className="ml-1 tabular-nums opacity-70" data-testid={`${testId}-count-${k}`}>{counts[k] ?? 0}</span>
        </button>
      ))}
    </div>
  );
}

// The standard coach response: Approve, Decline (immediate), Return (asks for a reason).
export function ReviewActions({ onApprove, onDecline, onReturn, testIds }) {
  const [busy, setBusy] = useState(false);
  const decline = async () => { setBusy(true); try { await onDecline(); } finally { setBusy(false); } };
  return (
    <div className="flex flex-wrap gap-1.5">
      <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={onApprove} data-testid={testIds.approve}>Approve</Button>
      <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" disabled={busy} onClick={decline} data-testid={testIds.decline}>
        {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}Decline
      </Button>
      <Button size="sm" variant="outline" className="rounded-full border-violet-200 text-violet-600 hover:bg-violet-50" onClick={onReturn} data-testid={testIds.return}>Return</Button>
    </div>
  );
}

function MoreSentinel({ hasMore, loading, onLoadMore, testId }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!hasMore || !ref.current) return undefined;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) onLoadMore(); }, { rootMargin: "300px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [hasMore, onLoadMore]);
  if (!hasMore && !loading) return null;
  return (
    <div ref={ref} className="flex justify-center py-3 text-xs text-slate-400" data-testid={`${testId}-more`}>
      {loading && <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading more…</span>}
    </div>
  );
}

// One card shared by Merit cases/requests, Lettering and Thespian review: title + filter badges, table, auto "load more".
export default function SubmissionReviewCard({ title, subtitle = "Sorted by member last name", icon: Icon, iconClass, filters, filter, onFilter, counts, columns, rowCount, emptyText, hasMore, loading, onLoadMore, testId, id, className = "mb-6", children }) {
  return (
    <Card id={id} className={`scroll-mt-24 overflow-hidden border-slate-200 shadow-sm ${className}`} data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2">
          {Icon && <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${iconClass}`}><Icon className="h-4 w-4" /></span>}
          <div>
            <h3 className="font-heading text-sm font-bold text-slate-800">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {filters?.length !== 0 && <ReviewFilters value={filter} onChange={onFilter} counts={counts} testId={testId} filters={filters} />}
      </div>
      {rowCount === 0 && !loading ? (
        <p className="px-4 py-10 text-center text-sm text-slate-400" data-testid={`${testId}-empty`}>{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid={`${testId}-table`}>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {columns.map((h) => <TableHead key={h} className={`text-xs font-bold uppercase tracking-wide text-slate-500 ${h === "Member" ? STICKY_HEAD : ""}`}>{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>{children}</TableBody>
          </Table>
        </div>
      )}
      <MoreSentinel hasMore={hasMore} loading={loading} onLoadMore={onLoadMore} testId={testId} />
    </Card>
  );
}

export const EMPTY_TEXT = {
  pending: "Nothing is waiting for your review.",
  approved: "No approved submissions yet.",
  declined: "No declined submissions.",
  all: "No submissions yet.",
};
